import {useRouter} from 'next/router';
import {t} from '../../../common/getTranslations';
import {TabPositionSticky} from '../../../common/presentational/TabPositionSticky';
import {requestContext} from '../../../common/requestContext';
import {FilterMenuOptionTab} from '../../../design-system/organisms/FilterMenuOptionTab/FilterMenuOptionTab';
import {useDialog} from '../../../dialog/useDialog.hook';
import {DayOfWeekLayout, FilterOption} from '../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../logging/aggregateAndSendLog';
import {useGetDayOfWeekEventLog} from '../hook/useGetDayOfWeekEventLog';
import {useGetDayOfWeekLayoutData} from '../hook/useGetDayOfWeekLayoutData';
import {useGetDayOfWeekQueryParams} from '../hook/useGetDayOfWeekQueryParams';

interface Props {
  tabId?: string;
  sortType?: string;
  dayOfWeekLayout: DayOfWeekLayout;
}

export const DayOfWeekLayoutMenuListAndSortTab = ({...rest}: Props) => {
  const {display, lang} = requestContext.requestInfo;
  const {isMobile, isWebView} = display;
  const {deviceLang} = lang;
  const {referenceList, sortList} = rest.dayOfWeekLayout;

  const layoutData = useGetDayOfWeekLayoutData(rest);
  const {currentReferenceItem, currentSortItem} = layoutData;

  const router = useRouter();
  const {getQueryString} = useGetDayOfWeekQueryParams();
  const eventLog = useGetDayOfWeekEventLog(rest);

  const tabItems = referenceList.map(item => ({
    name: item.translateProperty?.name?.[deviceLang] ?? item.name,
    param: item.param,
    href: getQueryString({
      tab_id: item.param,
      // 탭 메뉴 변경 시 정렬 정보와 페이징 정보를 초기화 합니다.
      sort_type: undefined,
      page: undefined,
    }).search,
  }));

  const {DialogWrapper, Dialog, showDialog, hideDialog} = useDialog({
    isMobile,
  });

  const handleShowSortListDialog = () => {
    aggregateAndSendLog({
      actionType: 'Event',
      actionName: '정렬선택_클릭',
      click: {
        layer1: '필터정렬',
      },
      customProps: {
        user_action: 'click',
        ...eventLog.getCustomProps(),
      },
    });

    showDialog();
  };

  const handleChangeReference = (selectedReferenceId: string) => {
    const changedReference = referenceList.find(
      item => item.param === selectedReferenceId
    );
    if (!changedReference) return;

    aggregateAndSendLog({
      actionType: 'Event',
      actionName: '필터_클릭',
      click: {
        layer1: '필터정렬',
        copy: changedReference.name,
      },
      customProps: {
        user_action: 'click',
        ...eventLog.getCustomProps(),
      },
    });
  };

  const handleChangeSort = (changedSortItem: FilterOption) => {
    aggregateAndSendLog({
      actionType: 'Event',
      actionName: '정렬_클릭',
      click: {
        layer1: '필터정렬',
        copy: changedSortItem.name,
      },
      customProps: {
        user_action: 'click',
        ...eventLog.getCustomProps(),
      },
    });

    const {pathname} = router;
    const {search} = getQueryString({
      sort_type: changedSortItem.param,
      // 정렬 변경 시 페이징 정보를 초기화 합니다.
      page: undefined,
    });

    router.push({pathname, search});

    hideDialog();
  };

  return (
    <TabPositionSticky depth={3} isMobile={isMobile} isWebView={isWebView}>
      <FilterMenuOptionTab
        tabItems={tabItems}
        selectedTab={currentReferenceItem.param}
        selectedSort={
          currentSortItem.translateProperty?.name?.[deviceLang] ??
          currentSortItem.name
        }
        onSelectTab={param => handleChangeReference(param)}
        onSelectSort={handleShowSortListDialog}
      />

      {/* 정렬 리스트 Dialog */}
      <DialogWrapper>
        <Dialog.Dimmed />
        <Dialog.List>
          {sortList.map(item => (
            <Dialog.Option
              key={item.param}
              isChecked={currentSortItem.param === item.param}
              onClick={() => handleChangeSort(item)}
            >
              {item.translateProperty?.name?.[deviceLang] ?? item.name}
            </Dialog.Option>
          ))}
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={hideDialog}>
            {t('common_cancel_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>
    </TabPositionSticky>
  );
};
